import React, { useEffect, useState } from 'react';
import { ArrowRight, Circle, Award, Clock, Users, Shield } from 'lucide-react';
import { services } from '../data/services';
import ScrollToTopLink from '../components/ScrollToTopLink';
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { cn } from "../lib/utils";

function ElegantShape({
  className,
  delay = 0,
  width = 400,
  height = 100,
  rotate = 0,
  gradient = "from-white/[0.08]",
}: {
  className?: string;
  delay?: number;
  width?: number;
  height?: number;
  rotate?: number;
  gradient?: string;
}) {
  return (
    <motion.div
      initial={{
        opacity: 0,
        y: -150,
        rotate: rotate - 15,
        scale: 0.8,
      }}
      animate={{
        opacity: 1,
        y: 0,
        rotate: rotate,
        scale: 1,
      }}
      whileHover={{ 
        scale: 1.05,
        rotate: rotate + 5,
        transition: { duration: 0.3 }
      }}
      transition={{
        duration: 1.4,
        delay,
        ease: [0.34, 1.56, 0.64, 1],
        opacity: { duration: 0.6 },
        scale: { duration: 1.2 }
      }}
      className={cn("absolute cursor-pointer", className)}
    >
      <motion.div
        animate={{
          y: [0, 15, 0],
          scale: [1, 1.02, 1],
        }}
        transition={{
          duration: 6,
          repeat: Number.POSITIVE_INFINITY,
          ease: "easeInOut",
          times: [0, 0.5, 1]
        }}
        style={{ width, height }}
        className="relative"
      >
        <div
          className={cn(
            "absolute inset-0 rounded-full",
            "bg-gradient-to-r to-transparent",
            gradient,
            "backdrop-blur-[3px] border-2 border-white/[0.2]",
            "shadow-[0_8px_32px_0_rgba(255,255,255,0.2)]",
            "after:absolute after:inset-0 after:rounded-full",
            "after:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.4),transparent_60%)]"
          )}
        />
      </motion.div>
    </motion.div>
  );
}

const CountingNumber = ({ value }: { value: string }) => {
  const numberValue = parseInt(value.replace(/\D/g, ''));
  const countRef = useMotionValue(0);
  const rounded = useTransform(countRef, (latest) => Math.round(latest));
  
  useEffect(() => {
    const controls = animate(countRef, numberValue, {
      duration: 2,
      ease: "easeOut",
    });
    return controls.stop;
  }, [numberValue, countRef]);

  return <motion.span>{rounded}</motion.span>;
};

const AnimatedNumber = ({ value }: { value: number }) => {
  const countRef = useMotionValue(0);
  const rounded = useTransform(countRef, Math.round);

  useEffect(() => {
    const controls = animate(countRef, value, {
      duration: 2,
      ease: "easeOut",
    });
    return controls.stop;
  }, [value, countRef]);

  return <motion.span>{rounded}</motion.span>;
};

const useCountUp = (end: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number | null = null;
    let animationFrame: number;

    const updateCount = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuad = (t: number) => t * (2 - t);
      const currentCount = Math.round(easeOutQuad(percentage) * end);
      
      setCount(currentCount);

      if (progress < duration) {
        animationFrame = requestAnimationFrame(updateCount);
      }
    };

    animationFrame = requestAnimationFrame(updateCount);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);

  return count;
};

const Home = () => {
  const isMobile = window.innerWidth < 768;
  const fadeUpVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        delay: 0.5 + i * 0.2,
        ease: [0.25, 0.4, 0.25, 1],
      },
    }),
  };

  return (
    <div className="min-h-screen">
      {/* Add an id to the top of the page */}
      <div id="top"></div>
      {/* Hero Section */}
      <section className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#030303] -mt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a237e]/[0.12] via-[#1a237e]/[0.08] to-[#0d47a1]/[0.12] blur-3xl" />

        <div className="absolute inset-0 overflow-hidden">
          <ElegantShape
            delay={0.3}
            width={600}
            height={140}
            rotate={12}
            gradient="from-blue-400/[0.25] via-blue-500/[0.2] to-transparent"
            className="left-[-10%] md:left-[-5%] top-[15%] md:top-[20%]"
          />
          <ElegantShape
            delay={0.5}
            width={500}
            height={120}
            rotate={-15}
            gradient="from-blue-300/[0.25] via-blue-400/[0.2] to-transparent"
            className="right-[-5%] md:right-[0%] top-[70%] md:top-[75%]"
          />
          <ElegantShape
            delay={0.4}
            width={300}
            height={80}
            rotate={-8}
            gradient="from-blue-400/[0.25] via-blue-500/[0.2] to-transparent"
            className="left-[5%] md:left-[10%] bottom-[5%] md:bottom-[10%]"
          />
          <ElegantShape
            delay={0.6}
            width={200}
            height={60}
            rotate={20}
            gradient="from-blue-500/[0.25] via-blue-600/[0.2] to-transparent"
            className="right-[15%] md:right-[20%] top-[10%] md:top-[15%]"
          />
          <ElegantShape
            delay={0.7}
            width={150}
            height={40}
            rotate={-25}
            gradient="from-blue-500/[0.25] via-blue-600/[0.2] to-transparent"
            className="left-[20%] md:left-[25%] top-[5%] md:top-[10%]"
          />
          <ElegantShape
            delay={0.8}
            width={250}
            height={70}
            rotate={15}
            gradient="from-blue-300/[0.25] via-blue-400/[0.2] to-transparent"
            className="right-[25%] bottom-[15%]"
          />
          <ElegantShape
            delay={0.9}
            width={180}
            height={50}
            rotate={-18}
            gradient="from-blue-400/[0.25] via-blue-500/[0.2] to-transparent"
            className="left-[35%] top-[40%]"
          />
          <ElegantShape
            delay={1}
            width={220}
            height={65}
            rotate={22}
            gradient="from-blue-500/[0.25] via-blue-600/[0.2] to-transparent"
            className="right-[40%] top-[30%]"
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 md:px-6 pt-20">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              custom={0}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] mb-8 md:mb-12"
            >
              <Circle className="h-2 w-2 fill-blue-500/80" />
              <span className="text-sm text-white/60 tracking-wide">
                EMS
              </span>
            </motion.div>

            <motion.div
              custom={1}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-b from-white to-white/80">
                  Expert Maintenance
                </span>
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white/90 to-blue-300">
                  Solutions
                </span>
              </h1>
            </motion.div>

            <motion.div
              custom={2}
              variants={fadeUpVariants}
              initial="hidden"
              animate="visible"
            >
              <p className="text-xl md:text-2xl text-white/40 mb-8">
                Votre partenaire de confiance en maintenance électrique et informatique
              </p>
              <ScrollToTopLink
                to="/contact"
                className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-md text-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Contactez-nous
                <ArrowRight className="ml-2" size={20} />
              </ScrollToTopLink>
            </motion.div>
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-transparent to-[#030303]/80 pointer-events-none" />
      </section>

      {/* Services Section */}
      <section className="py-8 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              Nos Services
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600">
              Des solutions complètes pour vos besoins en maintenance
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            {services.slice(0, 6).map((service) => (
              <ScrollToTopLink
                key={service.id}
                to={`/services/${service.id}`}
                className="bg-white p-4 md:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200"
              >
                <div className="text-blue-600 mb-3 md:mb-4 flex items-center justify-center md:justify-start">
                  {React.createElement(service.icon, { 
                    size: isMobile ? 24 : 32,
                    className: "shrink-0"
                  })}
                </div>
                <h3 className="text-lg md:text-xl font-semibold mb-2 text-center md:text-left">
                  {service.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 text-center md:text-left">
                  {service.description}
                </p>
              </ScrollToTopLink>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-12">
            <ScrollToTopLink
              to="/services"
              className="inline-flex items-center text-blue-600 font-medium hover:text-blue-700 text-sm md:text-base"
            >
              Voir tous nos services
              <ArrowRight className="ml-2" size={isMobile ? 16 : 20} />
            </ScrollToTopLink>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-8 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 md:mb-6 text-center lg:text-left">
                Pourquoi nous choisir ?
              </h2>
              <div className="space-y-4 md:space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Expertise technique</h3>
                  <p className="text-gray-600">
                    Notre équipe possède une expertise approfondie dans tous les domaines de la maintenance.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Service personnalisé</h3>
                  <p className="text-gray-600">
                    Nous adaptons nos solutions à vos besoins spécifiques.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Disponibilité 24/7</h3>
                  <p className="text-gray-600">
                    Notre équipe est disponible pour répondre à vos urgences.
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6 lg:mt-0">
              <img
                src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80"
                alt="Expertise technique"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* New Features Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi Choisir Expert Maintenance Solutions?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une expertise complète pour tous vos besoins en maintenance
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Expertise Technique",
                description: "Une équipe qualifiée avec plus de 15 ans d'expérience dans le domaine",
              },
              {
                icon: Clock,
                title: "Disponibilité 24/7",
                description: "Une équipe disponible en permanence pour répondre à vos urgences",
              },
              {
                icon: Shield,
                title: "Qualité Garantie",
                description: "Des solutions durables et un service de haute qualité",
              }
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                whileHover={{ 
                  scale: 1.03,
                  backgroundColor: "rgba(26, 35, 126, 0.02)",
                }}
                className="p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300
                  border border-transparent hover:border-[#1a237e]/10"
              >
                <motion.div 
                  className="w-12 h-12 bg-[#1a237e]/10 rounded-lg flex items-center justify-center mb-4"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {React.createElement(feature.icon, { 
                    className: "w-6 h-6 text-[#1a237e]"
                  })}
                </motion.div>
                <h3 className="text-xl font-semibold mb-2 text-[#1a237e]">{feature.title}</h3>
                <p className="text-gray-700">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-[#1a237e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 15, label: "Années d'expérience", suffix: "+" },
              { value: 500, label: "Clients satisfaits", suffix: "+" },
              { value: 24, label: "Support technique", suffix: "/7" },
              { value: 100, label: "Satisfaction client", suffix: "%" }
            ].map((stat, index) => {
              const count = useCountUp(stat.value, 2000);
              
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ 
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ scale: 1.05 }}
                  className="text-white"
                >
                  <motion.div 
                    className="text-4xl font-bold mb-2 flex items-center justify-center"
                    initial={{ y: 20 }}
                    whileInView={{ y: 0 }}
                    transition={{ delay: index * 0.2 }}
                  >
                    {count}{stat.suffix}
                  </motion.div>
                  <div className="text-white/80">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a237e] mb-4">
              Ce Que Disent Nos Clients
            </h2>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                number: 1,
                initials: "SA",
                company: "Société Atlantique",
                role: "Directeur Technique",
                quote: "Une équipe réactive et professionnelle. Leur expertise technique a permis d'optimiser significativement nos installations."
              },
              {
                number: 2,
                initials: "TI",
                company: "Tech Industries",
                role: "Responsable Maintenance",
                quote: "Disponibilité 24/7 et solutions rapides à nos problèmes. Un partenaire fiable pour notre maintenance."
              },
              {
                number: 3,
                initials: "GP",
                company: "Groupe Pacific",
                role: "Chef de Projet",
                quote: "Excellence technique et service client exemplaire. Nous recommandons vivement leurs services."
              }
            ].map((testimonial, index) => {
              const count = useCountUp(testimonial.number, 1500 + index * 500);
              
              return (
                <motion.div
                  key={testimonial.initials}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ 
                    scale: 1.02,
                    backgroundColor: "rgba(26, 35, 126, 0.02)"
                  }}
                  className="group bg-gray-50 p-6 rounded-xl shadow-md hover:shadow-xl 
                    transition-all duration-300 border border-transparent hover:border-[#1a237e]/10
                    cursor-pointer relative overflow-hidden"
                >
                  {/* Number Badge */}
                  <div 
                    className="absolute -top-6 -left-6 w-24 h-24 bg-[#1a237e]/5 rounded-full 
                      flex items-center justify-center text-4xl font-bold text-[#1a237e]/20 
                      transform -rotate-12"
                  >
                    {count}
                  </div>

                  <div className="flex items-center mb-4">
                    <motion.div 
                      className="w-12 h-12 bg-[#1a237e] rounded-full mr-4 flex items-center justify-center text-white font-bold"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {testimonial.initials}
                    </motion.div>
                    <div>
                      <motion.div 
                        className="font-semibold text-[#1a237e] group-hover:text-[#0d47a1]
                          transition-colors duration-300"
                      >
                        {testimonial.company}
                      </motion.div>
                      <div className="text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                  <motion.p 
                    className="text-gray-700 relative pl-4 border-l-2 border-[#1a237e]/20
                      group-hover:border-[#1a237e] transition-colors duration-300"
                  >
                    "{testimonial.quote}"
                  </motion.p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1a237e] to-[#0d47a1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à Optimiser Votre Infrastructure?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Bénéficiez d'une consultation gratuite avec nos experts
          </p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <ScrollToTopLink
                to="/contact"
                className="inline-flex items-center bg-white text-[#1a237e] px-8 py-3 rounded-lg
                  text-lg font-semibold hover:bg-gray-50 transition-all duration-200
                  hover:shadow-lg"
              >
                Demandez un devis gratuit
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  <ArrowRight className="ml-2" size={20} />
                </motion.div>
              </ScrollToTopLink>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;